import React, { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import styled from "@emotion/styled";
import PropTypes from "prop-types";
import SelectedFilters from "../Filters/SelectedFilters";
import FilterGroups from "./components/FilterGroups";
import useOnClickOutside from "../../hooks/useOnClickOutside";
import FilterControls from "./components/FilterControls/FilterControls";
import useUserAccess from "../../hooks/useUserAccess";

const MenuDropdown = styled.div(
  ({ theme }) => `
    display: flex;
    flex-direction: column;
    gap: 0.625rem;
    padding: 0.375rem 0.75rem 1.125rem;
    color: ${theme.menuPrimary ? theme.textOnPrimary : theme.text.secondary};
    border-bottom: 1px solid ${
      theme.menuPrimary ? "rgba(255,255, 255, 0.1)" : theme.divider
    };
`
);

const MenuDropdownTitle = styled.div(
  () => `
    display: flex;
    justify-content: space-between;
    font-weight: bold;
    font-size: 0.8em;
    text-transform: uppercase;
    cursor: pointer;
    text-align: left;
    user-select: none;

    & p {
        margin: 0;
    }
`
);

const Icon = styled.div`
  margin-right: 8px;
  font-size: 12px;
  width: 18px;
  text-align: center;
`;

const FilterGroupContainer = styled.div`
  position: relative;
  display: flex;
  font-size: 12px;
  text-transform: none;
  font-weight: 400;
`;

export default function FilterGroup(props) {
  const {
    name,
    open,
    children,
    cy,
    filter,
    select,
    additionalTheme,
    loadValues,
    selectedValues,
    isSingleQueryFilter,
  } = props;

  const [showFilters, setShowFilters] = useState(!!open);
  const [showGroups, setShowGroups] = useState(false);
  const loading = filter?.loading;
  const [valuesLoaded, setValuesLoaded] = useState(!!filter?.values);
  const filterType = filter?.type;

  const access = useUserAccess();

  const { groups, manageFromDashboard } = additionalTheme ?? {};
  const filterGroups = (groups ?? {})[filter?.uuid];

  const ref = useRef(null);
  useOnClickOutside(ref, () => setShowGroups(false));

  useEffect(() => {
    if (loadValues) {
      setValuesLoaded(false);
    }
  }, [loadValues, filterType]);

  useEffect(() => {
    (async () => {
      const pendingLazyLoad = loadValues && !loading;
      if (pendingLazyLoad && showFilters && !valuesLoaded) {
        try {
          await loadValues();
        } finally {
          setValuesLoaded(true);
        }
      }
    })();
  }, [loadValues, loading, showFilters, valuesLoaded]);

  function handleShowGroups(e) {
    e.stopPropagation();
    setShowGroups(!showGroups);
  }

  // do not show icon for control menu for non site config and date filters
  const showControls =
    manageFromDashboard &&
    access.filterControls &&
    filter &&
    !isSingleQueryFilter;

  return (
    <MenuDropdown data-cy="menu-dropdown" data-menu-filter-uuid={filter?.uuid}>
      <MenuDropdownTitle
        onClick={() => setShowFilters(!showFilters)}
        data-cy={cy}
      >
        <p>{name}</p>
        <FilterGroupContainer>
          <div ref={ref}>
            {!!filterGroups?.length && (
              <Icon onClick={handleShowGroups}>
                <FontAwesomeIcon
                  icon={["fal", showGroups ? "folder-open" : "folder"]}
                />
              </Icon>
            )}

            <FilterGroups
              showGroups={showGroups}
              filterGroups={filterGroups}
              select={select}
              filterValues={filter?.values}
            />
          </div>

          <Icon>
            <FontAwesomeIcon
              icon={["fas", showFilters ? "caret-up" : "caret-down"]}
            />
          </Icon>

          {showControls ? <FilterControls filter={filter} /> : null}
        </FilterGroupContainer>
      </MenuDropdownTitle>
      {showFilters ? children : null}

      <SelectedFilters
        filter={filter}
        select={select}
        showFilters={showFilters}
        additionalTheme={additionalTheme}
        selectedValues={selectedValues}
        isSingleQueryFilter={isSingleQueryFilter}
      />
    </MenuDropdown>
  );
}

FilterGroup.propTypes = {
  name: PropTypes.string.isRequired,
  open: PropTypes.bool,
};
